import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import { FeedbackSuccessView } from './FeedbackSuccessView';
import { StarRatingInput } from './StarRatingInput';
import { showErrorToast } from '../notifications/toast';
import { useResponsivePopupOptions } from '../shell/useResponsivePopup';
import type { SiteFeedbackApi, SiteSupportCopy } from './types';

const MAX_MESSAGE = 4000;
const FALLBACK_TYPES = ['General', 'Feature', 'Change'];
const FALLBACK_PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];

function isGeneralType(type: string): boolean {
  return type.trim().toLowerCase() === 'general';
}

function isTicketType(type: string): boolean {
  const t = type.trim().toLowerCase();
  return t === 'feature' || t === 'change';
}

export interface ShareFeedbackPopupProps {
  visible: boolean;
  onClose: () => void;
  api: SiteFeedbackApi;
  copy: SiteSupportCopy;
}

export function ShareFeedbackPopup({ visible, onClose, api, copy }: ShareFeedbackPopupProps) {
  const [rating, setRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState('General');
  const [feedbackTypes, setFeedbackTypes] = useState<string[]>(FALLBACK_TYPES);
  const [priorities, setPriorities] = useState<string[]>(FALLBACK_PRIORITIES);
  const [priority, setPriority] = useState('Medium');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const general = useMemo(() => isGeneralType(feedbackType), [feedbackType]);
  const ticketLike = useMemo(() => isTicketType(feedbackType), [feedbackType]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void Promise.all([api.listActiveMeta('FeedbackType'), api.listActiveMeta('Priority')])
      .then(([typeRows, priorityRows]) => {
        if (cancelled) return;
        const typeNames = typeRows.map((r) => r.name);
        if (typeNames.length > 0) {
          setFeedbackTypes(typeNames);
          setFeedbackType((prev) =>
            typeNames.includes(prev) ? prev : typeNames.includes('General') ? 'General' : typeNames[0]
          );
        }
        const priorityNames = priorityRows.map((r) => r.name);
        if (priorityNames.length > 0) {
          setPriorities(priorityNames);
          setPriority((prev) =>
            priorityNames.includes(prev)
              ? prev
              : priorityNames.includes('Medium')
                ? 'Medium'
                : priorityNames[0]
          );
        }
      })
      .catch(() => {
        /* keep fallbacks */
      });
    return () => {
      cancelled = true;
    };
  }, [api, visible]);

  const reset = useCallback(() => {
    setRating(0);
    setFeedbackType(feedbackTypes.includes('General') ? 'General' : feedbackTypes[0] ?? 'General');
    setPriority(priorities.includes('Medium') ? 'Medium' : priorities[0] ?? 'Medium');
    setSubject('');
    setMessage('');
    setSuccess(false);
  }, [feedbackTypes, priorities]);

  const handleHide = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const submit = useCallback(async () => {
    if (!feedbackType.trim()) {
      showErrorToast('Please select a feedback type.');
      return;
    }
    if (isGeneralType(feedbackType) && rating < 1) {
      showErrorToast('Please select a star rating.');
      return;
    }
    if (isTicketType(feedbackType) && !subject.trim()) {
      showErrorToast('Title is required for Feature and Change requests.');
      return;
    }
    if (!message.trim()) {
      showErrorToast(isTicketType(feedbackType) ? 'Description is required.' : 'Your comment is required.');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitComment({
        message: message.trim(),
        starRating: isGeneralType(feedbackType) ? rating : undefined,
        feedbackType,
        subject: isTicketType(feedbackType) ? subject.trim() : undefined,
        priority: isTicketType(feedbackType) ? priority : undefined,
        pageUrl: window.location.href
      });
      setSuccess(true);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Submit failed.');
    } finally {
      setSubmitting(false);
    }
  }, [api, feedbackType, message, priority, rating, subject]);

  const popupOptions = useResponsivePopupOptions({ width: 640, height: 'auto' });

  return (
    <Popup
      visible={visible}
      onHiding={handleHide}
      showTitle
      title={copy.feedbackTitle ?? 'Share Your Feedback'}
      {...popupOptions}
      maxHeight="90vh"
      dragEnabled={false}
      hideOnOutsideClick={!submitting}
    >
      {success ? (
        <FeedbackSuccessView kind="feedback" />
      ) : (
        <div className="site-support-form">
          <label className="site-support-label">
            Type <span className="site-support-required">*</span>
          </label>
          <SelectBox
            items={feedbackTypes}
            value={feedbackType}
            onValueChanged={(e) => setFeedbackType(String(e.value ?? feedbackTypes[0] ?? 'General'))}
          />

          {general ? (
            <>
              <label className="site-support-label">How would you rate your experience?</label>
              <div className="site-support-rating-row">
                <StarRatingInput value={rating} onChange={setRating} />
                <span className="site-support-hint">
                  {rating > 0 ? `${rating} star${rating === 1 ? '' : 's'}` : 'Please select'}
                </span>
              </div>
            </>
          ) : null}

          {ticketLike ? (
            <>
              <label className="site-support-label">
                Title <span className="site-support-required">*</span>
              </label>
              <TextBox
                value={subject}
                maxLength={100}
                onValueChanged={(e) => setSubject(String(e.value ?? ''))}
                placeholder="Short summary of the feature or change"
              />

              <label className="site-support-label">Priority</label>
              <SelectBox
                items={priorities}
                value={priority}
                onValueChanged={(e) => setPriority(String(e.value ?? priorities[0] ?? 'Medium'))}
              />
            </>
          ) : null}

          <label className="site-support-label">
            {ticketLike ? 'Description' : 'Your comment'} <span className="site-support-required">*</span>
          </label>
          <TextArea
            value={message}
            height={140}
            maxLength={MAX_MESSAGE}
            onValueChanged={(e) => setMessage(String(e.value ?? ''))}
            placeholder={
              ticketLike
                ? 'Describe the request, goals, and any acceptance criteria...'
                : 'Tell us what you liked, what could be better, or share any suggestions...'
            }
          />
          <div className="site-support-char-count">
            {message.length}/{MAX_MESSAGE}
          </div>

          <div className="site-support-info-banner">
            {ticketLike
              ? 'Feature and change requests become tickets you can track and discuss on My Tickets.'
              : 'Your feedback helps us prioritize improvements and fix what matters most.'}
          </div>

          <div className="site-support-form-actions">
            <Button text="Cancel" stylingMode="outlined" onClick={handleHide} disabled={submitting} />
            <Button text="Submit Feedback" type="default" onClick={() => void submit()} disabled={submitting} />
          </div>
        </div>
      )}
    </Popup>
  );
}
